import { useParams, Link } from 'react-router';
import { useState, useEffect } from 'react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { customersApi, type Customer, type CustomerAppointment } from '@/api/customers';
import { formatTime } from '@/utils/time';
import { formatDateShort } from '@/utils/date';
import { StatusBadge, type StatusType } from '../components/status-badge';
import { Dna, Loader2 } from 'lucide-react';

function mapStateToStatus(stateCode: string): StatusType {
  const code = stateCode.toUpperCase();
  if (code === 'COMPLETED') return 'completed';
  if (code === 'CONFIRMED') return 'confirmed';
  if (code === 'PENDING') return 'pending';
  if (code === 'IN_PROGRESS' || code === 'IN-PROGRESS') return 'in-progress';
  if (code === 'CANCELLED') return 'cancelled';
  if (code === 'NO_SHOW' || code === 'NOSHOW') return 'noshow';
  if (code === 'EXPIRED') return 'expired';
  return 'upcoming';
}

export function CustomerHistory() {
  const { customerId } = useParams();
  const businessId = useBusinessId();
  const [filter, setFilter] = useState<'all' | 'completed' | 'upcoming'>('all');

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [appointments, setAppointments] = useState<CustomerAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId || !customerId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [cust, apts] = await Promise.all([
          customersApi.getById(businessId, Number(customerId)),
          customersApi.getAppointments(businessId, Number(customerId)),
        ]);
        setCustomer(cust);
        setAppointments(apts);
      } catch (err) {
        console.error('Error al cargar historial:', err);
        setError('No se pudo cargar el historial del cliente.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [businessId, customerId]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--muted-fg)', fontSize: '14px' }}>
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando historial...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <div style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>{error || 'Cliente no encontrado'}</div>
      </div>
    );
  }

  const fullName = `${customer.firstName} ${customer.lastName}`.trim();
  const totalVisits = appointments.filter(a => a.stateCode?.toUpperCase() === 'COMPLETED').length;

  const filteredAppointments = appointments
    .filter(a => {
      const code = a.stateCode?.toUpperCase() || '';
      if (filter === 'completed') return code === 'COMPLETED';
      if (filter === 'upcoming') return code === 'CONFIRMED' || code === 'PENDING' || code === 'IN_PROGRESS';
      return true;
    })
    .sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime());

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  /* --- Filter bar --- */
  const filterLabels: Record<string, string> = { all: 'Todas', completed: 'Completadas', upcoming: 'Proximas' };
  const filterBar = (
    <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
      {(['all', 'completed', 'upcoming'] as const).map(f => (
        <button
          key={f}
          onClick={() => setFilter(f)}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: filter === f ? 'var(--terracotta)' : 'transparent',
            color: filter === f ? 'white' : 'var(--dark-teal)',
            fontWeight: filter === f ? 500 : 400,
            cursor: 'pointer',
          }}
        >
          {filterLabels[f]}
        </button>
      ))}
    </div>
  );

  /* --- Appointment cards --- */
  const appointmentsList = filteredAppointments.length === 0 ? (
    <div className="rounded-xl flex items-center justify-center"
      style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '40px', minHeight: '200px' }}>
      <div style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>No se encontraron citas</div>
    </div>
  ) : (
    <div className="space-y-3">
      {filteredAppointments.map(apt => (
        <Link key={apt.id} to={`/appointment/${apt.id}`} className="block">
          <div className="rounded-2xl transition-all hover:shadow-lg"
            style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: '0 2px 12px rgba(36, 62, 61, 0.08), 0 1px 3px rgba(36, 62, 61, 0.04)', padding: '16px 20px' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="hidden md:block" style={{ width: '80px', fontSize: '12px', color: 'var(--muted-fg)', flexShrink: 0 }}>{formatDateShort(apt.appointmentDate)}</div>
                <div className="min-w-0">
                  <div style={{ fontSize: '14px', color: 'var(--dark-teal)', fontFamily: 'var(--font-heading)' }}>{apt.serviceName}</div>
                  <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                    <span className="md:hidden">{formatDateShort(apt.appointmentDate)}</span>
                    <span className="md:hidden" style={{ opacity: 0.3 }}>|</span>
                    <span>{apt.startTime ? formatTime(apt.startTime) : '--'}</span>
                    <span style={{ opacity: 0.3 }}>|</span>
                    <span>{apt.userName}</span>
                  </div>
                </div>
              </div>
              <StatusBadge status={mapStateToStatus(apt.stateCode)} size="sm" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>

      {/* --- MOBILE LAYOUT (< 768px) --- */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto pb-24">
          {/* Compact Client Header */}
          <div style={{ backgroundColor: 'var(--dark-teal)', padding: '14px 16px' }}>
            <div className="flex items-center gap-3">
              <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '44px', height: '44px', backgroundColor: 'var(--terracotta)' }}>
                <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{getInitials(fullName)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate" style={{ fontFamily: 'var(--font-heading)', fontSize: '16px' }}>{fullName}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Cliente</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>·</span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{totalVisits} visitas</span>
                </div>
              </div>
              <Link to={`/client-dna/${customerId}`} className="flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ width: '36px', height: '36px', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Dna size={15} style={{ color: 'rgba(255,255,255,0.7)' }} />
              </Link>
            </div>

            {customer.phone && (
              <div className="flex items-center gap-2 mt-2 min-w-0" style={{ fontSize: '11px' }}>
                <span className="flex-shrink-0" style={{ color: 'var(--peach)' }}>{customer.phone}</span>
                {customer.email && (
                  <>
                    <span className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                    <span className="truncate min-w-0" style={{ color: 'rgba(255,255,255,0.6)' }}>{customer.email}</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile Content */}
          <div style={{ padding: '12px 14px 24px' }}>
            <div className="flex items-center justify-between mb-3">
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--dark-teal)' }}>Historial</div>
              {filterBar}
            </div>
            {appointmentsList}
          </div>
        </div>
      </div>

      {/* --- DESKTOP/TABLET LAYOUT (>= 768px) --- */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* LEFT -- Client Card */}
        <div className="flex flex-col overflow-auto" style={{ width: '300px', minWidth: '300px', backgroundColor: 'var(--dark-teal)', padding: '24px 20px 16px' }}>
          <div className="flex flex-col items-center mb-4">
            <div className="rounded-full flex items-center justify-center" style={{ width: '80px', height: '80px', backgroundColor: 'var(--terracotta)' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '24px' }}>{getInitials(fullName)}</span>
            </div>
          </div>

          <div className="text-center mb-4">
            <div className="text-white mb-1" style={{ fontFamily: 'var(--font-heading)', fontSize: '18px' }}>{fullName}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
              Cliente
            </div>
          </div>

          <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
            <div className="space-y-2">
              <div className="flex justify-between" style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.45)' }}>Total visitas</span>
                <span style={{ color: 'rgba(255,255,255,0.8)' }}>{totalVisits}</span>
              </div>
              {customer.phone && (
                <div className="flex justify-between" style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>Teléfono</span>
                  <span style={{ color: 'var(--peach)' }}>{customer.phone}</span>
                </div>
              )}
              {customer.email && (
                <div className="flex justify-between gap-2 min-w-0" style={{ fontSize: '12px' }}>
                  <span className="flex-shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }}>Email</span>
                  <span className="truncate min-w-0 text-right" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>{customer.email}</span>
                </div>
              )}
            </div>
          </div>

          {customer.notes && (
            <div className="rounded-lg mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', padding: '10px 12px' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', fontWeight: 500 }}>Notas</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{customer.notes}</div>
            </div>
          )}

          <div className="mt-auto">
            <Link to={`/client-dna/${customerId}`} className="flex items-center justify-center gap-1.5 rounded-lg transition-colors hover:bg-white/10"
              style={{ height: '36px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              <Dna size={13} /> Ver ADN del Cliente
            </Link>
          </div>
        </div>

        {/* RIGHT -- Appointment History */}
        <div className="flex-1 overflow-auto" style={{ padding: '20px 24px 16px' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>Historial de Citas</div>
              <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Todas las visitas con todos los profesionales</div>
            </div>
            {filterBar}
          </div>
          {appointmentsList}
        </div>
      </div>
    </div>
  );
}
